"use client";

import dynamic from "next/dynamic";
import { usePostHog } from "posthog-js/react";
import { useState, useTransition } from "react";
import { PostApplyPushPrompt } from "@/components/push/post-apply-push-prompt";
import { reloadOncePerBuild } from "@/lib/error-recovery/reload-budget";
import {
	type CoachingMoment,
	decideCoachingMoment,
} from "@/lib/hunt/coaching-moments";
import type { Locale } from "@/lib/i18n/config";
import { isServerActionSkewError } from "@/lib/profile/server-action-skew";
import { upsertApplication } from "@/server/actions/applications";

// Client-only (`ssr: false`) — see tracker-interview-coach.tsx for why.
const MockInterviewOfferCard = dynamic(
	() =>
		import("@/components/hunt/MockInterviewOfferCard").then(
			(m) => m.MockInterviewOfferCard,
		),
	{ ssr: false },
);

/** Shows the post-apply coaching offer at most once per browser session —
 * marking several jobs "applied" in one sitting shouldn't repeat the nudge. */
const POST_APPLY_COACHING_SESSION_KEY = "coaching_offer_post_apply_shown";

type Props = {
	jobId: string;
	/** When true, the caller has server-verified the job is already tracked
	 * as `applied` (or later) — we render the "Applied ✓" state instead of
	 * the primary CTA. */
	initiallyApplied?: boolean;
	labelIdle?: string;
	labelApplied?: string;
	labelSaving?: string;
	labelRetry?: string;
	className?: string;
	/** Job title — personalizes the post-apply coaching-offer copy/modal.
	 * The offer is skipped entirely when this or `locale` is omitted. */
	jobTitle?: string;
	locale?: Locale;
	/** Server-resolved `hunt-coaching-surfaces` flag. Fails closed to `false`. */
	coachingEnabled?: boolean;
	hasResume?: boolean;
	coachingSessionsThisMonth?: number;
	/** Server-resolved `web-push` flag — enables the one-time post-apply push
	 * opt-in right after this button flips the tracker row to "applied". This
	 * is the highest-traffic "applied" transition on the site (the primary
	 * apply CTA on every job detail page), unlike the narrower followup-list /
	 * offsite-confirm call sites — see `PostApplyPushPrompt`. */
	webPushEnabled?: boolean;
};

/**
 * Minimal one-click "Mark as applied" button. Creates or updates the
 * application_tracker row for the current user × job at status="applied",
 * optimistically flipping to the confirmed state so the user gets instant
 * feedback. Mirrors the SaveJobButton interaction model.
 *
 * Rendered on the job detail page next to the external Apply CTA and on
 * the saved-jobs list. Re-clicking while already applied is a no-op
 * at the UI level — the user changes status from the /applications
 * Kanban board.
 *
 * Right after a successful confirm, offers the free mock-interview coaching
 * surface (`decideCoachingMoment`, `justApplied: true`) — see
 * `src/lib/hunt/coaching-moments.ts` — and, when `webPushEnabled`, the
 * post-apply push opt-in (`justApplied` state below, same pattern as
 * `ClickedFollowupList`/`OffsiteApplyConfirmPrompt`: a boolean flipped only
 * on a fresh confirm this render, never on the `initiallyApplied` mount, so
 * revisiting an already-applied job never re-shows the prompt).
 */
export function MarkAsAppliedButton({
	jobId,
	initiallyApplied = false,
	labelIdle = "Mark as applied",
	labelApplied = "Applied ✓",
	labelSaving = "Saving…",
	labelRetry = "Try again",
	className,
	jobTitle,
	locale,
	coachingEnabled = false,
	hasResume = false,
	coachingSessionsThisMonth = 0,
	webPushEnabled = false,
}: Props) {
	const [applied, setApplied] = useState(initiallyApplied);
	const [justApplied, setJustApplied] = useState(false);
	const [actionFailed, setActionFailed] = useState(false);
	const [pending, startTransition] = useTransition();
	const [coachingMoment, setCoachingMoment] = useState<CoachingMoment | null>(
		null,
	);
	const posthog = usePostHog();

	const maybeOfferCoaching = () => {
		if (!coachingEnabled || !locale || !jobTitle) return;
		try {
			if (
				typeof window !== "undefined" &&
				window.sessionStorage.getItem(POST_APPLY_COACHING_SESSION_KEY)
			) {
				return;
			}
			const moment = decideCoachingMoment({
				justApplied: true,
				hasResume,
				sessionsThisMonth: coachingSessionsThisMonth,
			});
			if (!moment) return;
			window.sessionStorage.setItem(POST_APPLY_COACHING_SESSION_KEY, "1");
			setCoachingMoment(moment);
		} catch {
			// sessionStorage unavailable (SecurityError, private mode) — the
			// coaching offer is a nice-to-have, never block on it.
		}
	};

	const handleClick = () => {
		if (applied || pending) return;
		setActionFailed(false);
		posthog?.capture("application_marked_applied", { job_id: jobId });
		startTransition(async () => {
			setApplied(true);
			try {
				const res = await upsertApplication(jobId, "applied");
				if ("error" in res) {
					// Rollback on error — keeps the UI honest; silent is ok since
					// most errors here (unauth) redirect to login elsewhere.
					setApplied(false);
					return;
				}
				setJustApplied(true);
				maybeOfferCoaching();
			} catch (error) {
				setApplied(false);
				if (isServerActionSkewError(error) && reloadOncePerBuild("action")) {
					return;
				}
				setActionFailed(true);
			}
		});
	};

	return (
		<>
			<button
				type="button"
				disabled={pending || applied}
				onClick={handleClick}
				aria-pressed={applied}
				className={
					className ??
					"inline-flex items-center gap-1.5 rounded-lg border border-border bg-card px-3 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-default disabled:opacity-80"
				}
			>
				{pending
					? labelSaving
					: applied
						? labelApplied
						: actionFailed
							? labelRetry
							: labelIdle}
			</button>
			{coachingMoment && locale && jobTitle ? (
				<MockInterviewOfferCard
					moment={coachingMoment}
					jobId={jobId}
					jobTitle={jobTitle}
					locale={locale}
				/>
			) : null}
			{justApplied && webPushEnabled && locale ? (
				<PostApplyPushPrompt locale={locale} source="mark_as_applied" />
			) : null}
		</>
	);
}
